/** Shared auth types for the (app) guest-first flow (wiki/AUTH-DESIGN.md). */

/** Temporary AWS credentials from the Identity Pool (guest or authed). */
export interface AwsCredentials {
  accessKeyId: string;
  secretAccessKey: string;
  sessionToken: string;
  /** Epoch millis when these credentials expire, if known. */
  expiresAt?: number;
}

/** User Pool tokens from a completed SRP sign-in. */
export interface AuthTokens {
  /** ID token — carries the `email` claim the backend reads; sent as Bearer. */
  idToken: string;
  accessToken: string;
  refreshToken?: string;
  /** Epoch millis when the access/id tokens expire, if known. */
  expiresAt?: number;
}

/** Where the session flow currently sits. */
export type AuthStatus = 'loading' | 'guest' | 'authed';

/**
 * The persisted, reload-surviving slice of auth state. The `identityId` is the
 * uid actually in effect for this device: identical to the cached guest uid on
 * the common (same-device) path, but a DIFFERENT identity after a cross-device
 * conflict resolves — `identityId` is always the one to sign authed API calls
 * with, never the cached guest uid.
 */
export interface PersistedSession {
  identityId: string;
  email: string | null;
  tokens: AuthTokens | null;
  /**
   * Stale guest uid awaiting merge into this account, set only after a
   * cross-device identity conflict resolved to a different `identityId`.
   * Persisted (not just kept in memory) so the pending merge survives a
   * reload between signup and the next successful `session/complete`.
   */
  pendingMergeGuestUid?: string | null;
}

/**
 * The signed-in session cannot be renewed: the refresh token is spent or revoked, so nothing but a
 * fresh sign-in will restore it. Distinct from an ordinary request failure because the answer is
 * different — this one is the learner's to act on, and the app must say so rather than showing a
 * status code (2026-08-26).
 */
export class SessionExpiredError extends Error {
  constructor() {
    super('Your session has expired. Sign in again to keep your progress.');
    this.name = 'SessionExpiredError';
  }
}
